import moment from 'moment';
import { getStringDateWithSlashes } from 'helpers/chartTabs';
import { DAYS_OF_WEEK } from 'const/ui';
import { ABSENCES_FILTER_DATE_FORMAT, DATE_FORMAT } from 'const';

export const getDatesSequence = (startDateIso, endDateIso) => {
  const datesArray = [];
  const startDate = moment(startDateIso);
  const endDate = moment(endDateIso);

  while (startDate.isSameOrBefore(endDate)) {
    datesArray.push(getStringDateWithSlashes(startDate.toDate().toISOString()));
    startDate.add(1, 'days');
  }
  return datesArray;
};

const getFirstThreeLettersOfString = (string) => string.slice(0, 3);

export const getDayOfWeekNameByIndex = (index) => {
  const WEEK_DAYS_FROM_SUNDAY = [DAYS_OF_WEEK[6], ...DAYS_OF_WEEK.slice(0, 6)];
  if (index < -1 || index > 6) return null;
  return getFirstThreeLettersOfString(WEEK_DAYS_FROM_SUNDAY[index].label);
};

export const dateStringIntoDate = (dateWithSlashes) => {
  const [day, month, year] = dateWithSlashes.split('/');
  return new Date(`${year}-${month}-${day}`);
};

export const formatEmployeeToLeaveModal = (employee) => ({
  key: employee.employeeid,
  employeeid: employee.employeeid,
  siret: employee.siret,
  schedule: employee.scheduleId,
  name: `${employee.firstname1} ${employee.lastname}`,
});

export const mapEmployeesToCalendarPage = (employees) =>
  employees.map((emp) => ({
    employeeid: emp.employeeid,
    siret: emp.siret,
    scheduleId: emp.scheduleId,
    firstname1: emp.firstname1,
    lastname: emp.lastname,
    startdate: emp.startdate,
    enddate: emp.enddate,
  }));

export const getTooltipAddressForBranch = (branch) => {
  const address = [];
  if (branch.waynumber1 || branch.waynumber2) {
    address.push(branch.waynumber1 || branch.waynumber2);
  }
  if (branch.wayname || branch.wayname2) {
    address.push(branch.wayname || branch.wayname2);
  }
  if (branch.postcode) {
    address.push(branch.postcode);
  }
  if (branch.city) {
    address.push(branch.city);
  }
  return address;
};

export const mapCompanyBranchesToSelect = (branches) => {
  const result = new Array(branches.length);
  for (let i = 0; i < branches.length; i++) {
    const branch = branches[i];
    result[i] = {
      ...branch,
      value: branch.siret,
      label: branch.siret,
      paleLabel: `-- ${branch.wayname}, ${branch.waynumber1}, ${branch.city}`,
      tooltipLabelsArray: getTooltipAddressForBranch(branch),
    };
  }
  return result;
};

const formatDatesToFilter = (date) =>
  moment(date, DATE_FORMAT).format(ABSENCES_FILTER_DATE_FORMAT);

const isCurrentAbsence = ({ absences, date, employeeId }, customComparator) => {
  const targetDate = moment(formatDatesToFilter(date));

  for (let i = 0; i < absences?.length; i++) {
    const absence = absences[i];
    if (absence.employeeId === employeeId) {
      const start = moment(absence.start);
      const end = moment(absence.end);

      if (targetDate.isBetween(start, end, 'day', '[]')) {
        if (
          customComparator({ absence, start: absence.start, end: absence.end })
        ) {
          return true;
        }
      }
    }
  }
  return false;
};

export const isAbsenceInMorning = ({ absences, date, employeeId }) =>
  isCurrentAbsence(
    { absences, date, employeeId },
    ({ absence, start, end }) => {
      const targetDate = moment(formatDatesToFilter(date));
      const startMoment = moment(start);
      const endMoment = moment(end);

      if (
        targetDate.isSame(startMoment, 'day') &&
        absence.startTimeOfTheDay === 'morning'
      ) {
        return true;
      }
      if (
        targetDate.isSame(endMoment, 'day') &&
        absence.endTimeOfTheDay === 'morning'
      ) {
        return true;
      }
      return false;
    }
  );

export const isAbsenceInAfternoon = ({ absences, date, employeeId }) =>
  isCurrentAbsence(
    { absences, date, employeeId },
    ({ absence, start, end }) => {
      const targetDate = moment(formatDatesToFilter(date));
      const startMoment = moment(start);
      const endMoment = moment(end);

      if (
        targetDate.isSame(startMoment, 'day') &&
        absence.startTimeOfTheDay === 'afternoon'
      ) {
        return true;
      }
      if (
        targetDate.isSame(endMoment, 'day') &&
        absence.endTimeOfTheDay === 'afternoon'
      ) {
        return true;
      }
      return false;
    }
  );

export const isAbsencePartial = ({ absences, date, employeeId }) =>
  isCurrentAbsence(
    { absences, date, employeeId },
    ({ absence, start, end }) => {
      const targetDate = moment(formatDatesToFilter(date));
      const startMoment = moment(start);
      const endMoment = moment(end);

      if (
        targetDate.isSame(startMoment, 'day') &&
        absence.startTimeOfTheDay !== 'whole_day'
      ) {
        return true;
      }
      if (
        targetDate.isSame(endMoment, 'day') &&
        absence.endTimeOfTheDay !== 'whole_day'
      ) {
        return true;
      }
      return false;
    }
  );

export const isAbsenceWithAttachment = ({ absences, date, employeeId }) =>
  isCurrentAbsence(
    { absences, date, employeeId },
    ({ absence }) => Object.keys(absence.supportingDoc).length !== 0
  );

export const isCurrentAbsenceLoading = ({ absences, date, employeeId }) =>
  isCurrentAbsence(
    { absences, date, employeeId },
    ({ absence }) => !absence.id
  );

export const mapAbsencesToDates = ({ dates, absences }) => {
  const result = {};
  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    const targetDate = moment(formatDatesToFilter(date));
    const matchingAbsences = [];

    for (let j = 0; j < absences.length; j++) {
      const absence = absences[j];
      if (
        targetDate.isBetween(
          moment(absence.start),
          moment(absence.end),
          'day',
          '[]'
        )
      ) {
        matchingAbsences.push(absence);
      }
    }
    result[date] = matchingAbsences;
  }
  return result;
};

export const isAbsenceInDates = (absence, dates) => {
  const start = moment(absence.start);
  const end = moment(absence.end);

  for (let i = 0; i < dates.length; i++) {
    if (
      moment(formatDatesToFilter(dates[i])).isBetween(start, end, 'day', '[]')
    ) {
      return true;
    }
  }
  return false;
};

export { formatDatesToFilter };
